import { useMemo } from 'react';
import { Upload } from 'lucide-react';
import type { DocumentFolder, UploadLinkConfig } from '@peles/shared';
import { UPLOAD_FILE_TYPES } from '@peles/shared';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useDocumentFolders } from '../../hooks/useDocumentFolders';

/**
 * "Ask for a file back", with somewhere to put it and something to expect
 * (COMMUNICATIONS-DESIGN C7).
 *
 * ⚠️ **Built once for every composer, not per feature.** The vendor email had
 * this as a bare checkbox and nothing else could reach it — which is why the
 * feature existed for months without being findable. It drops into
 * `ComposeEmailModal`'s `extraOptions` slot, so any email gains it by passing
 * this rather than by growing its own.
 *
 * ⚠️ **A folder is not required.** An email about a record can attach the file
 * to that record and file it nowhere in particular, exactly as before; a folder
 * only adds where it lands. What is required is that at least *one* of the two
 * exists, and the caller — not this component — owns the record half, so the
 * server refuses the empty case rather than this trying to.
 */
export interface UploadLinkOptionsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  config: UploadLinkConfig;
  onConfigChange: (config: UploadLinkConfig) => void;
  /** True when the email is about a record, so a folder is genuinely optional. */
  hasRecordTarget?: boolean;
  disabled?: boolean;
}

/** "Events / Smith Wedding" — a name alone is ambiguous across branches. */
export function folderPaths(folders: DocumentFolder[]): { id: string; path: string }[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));

  function pathOf(folder: DocumentFolder, seen: Set<string>): string {
    // `seen` guards a parent cycle: the tree should not have one, but a render
    // that hangs is a much worse failure than a slightly wrong label.
    if (!folder.parentId || seen.has(folder.parentId)) return folder.name;
    const parent = byId.get(folder.parentId);
    if (!parent) return folder.name;
    seen.add(folder.parentId);
    return `${pathOf(parent, seen)} / ${folder.name}`;
  }

  return folders
    .map((folder) => ({ id: folder.id, path: pathOf(folder, new Set([folder.id])) }))
    .sort((a, b) => a.path.localeCompare(b.path));
}

export function UploadLinkOptions({
  enabled,
  onEnabledChange,
  config,
  onConfigChange,
  hasRecordTarget = false,
  disabled = false,
}: UploadLinkOptionsProps) {
  // One request for the whole tree: the endpoint returns every folder when no
  // parent is named, and paths are cheaper to build here than to walk over the
  // wire one level at a time.
  const { data: folders = [] } = useDocumentFolders({});
  const options = useMemo(() => folderPaths(folders), [folders]);

  return (
    <div className="space-y-3">
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          data-testid="include-upload-link"
        />
        <span>
          <span className="flex items-center gap-1 font-medium">
            <Upload size={13} />
            Ask for a file back
          </span>
          <span className="text-xs text-gray-500">
            Adds a button each recipient can use to send you a file — no login needed.
          </span>
        </span>
      </label>

      {enabled && (
        <div className="pl-6 space-y-3" data-testid="upload-link-config">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              File it in{hasRecordTarget ? ' (optional)' : ''}
            </label>
            <SearchableListSelect
              items={options}
              value={config.folderId ?? null}
              onChange={(value) =>
                onConfigChange({
                  ...config,
                  folderId: typeof value === 'string' && value ? value : null,
                })
              }
              getValue={(option) => option.id}
              getLabel={(option) => option.path}
              placeholder={hasRecordTarget ? 'Attach to the record only' : 'Choose a folder…'}
              disabled={disabled}
            />
            {!hasRecordTarget && !config.folderId && (
              <p className="mt-1 text-xs text-amber-600" data-testid="folder-required">
                Choose a folder so the file has somewhere to go.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Accept (leave empty for any file)
            </label>
            <SearchableListSelect
              items={[...UPLOAD_FILE_TYPES]}
              value={config.fileTypes ?? []}
              onChange={(value) =>
                onConfigChange({ ...config, fileTypes: Array.isArray(value) ? value : [value] })
              }
              getValue={(type) => type.key}
              getLabel={(type) => type.label}
              getSubtitle={(type) => type.extensions.join(' ')}
              placeholder="Any file"
              multiple
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}
